/**
 * Global `beforeEach` that guarantees an access token is in the store before any
 * route guard or page can call the API.
 *
 * Vuex is plain in-memory state, so `user.token` is empty on every hard load, and
 * router guards resolve inside `router.isReady()` — before `main.js` commits one.
 * Anything that calls the API in that window sends an empty Bearer, gets a 401,
 * and (until the 401 interceptor learned to recover) lost the member's session.
 *
 * Nothing was closing that window globally. `authGuard` is Auth0's own
 * `createAuthGuard`: it decides whether to redirect to login, and never touches
 * the store. The `if (auth0?.isAuthenticated?.value)` block that used to sit at
 * the bottom of router/index.js looked like the safety net but was not one — it
 * ran once at module-import time, and main.js imports the router before it
 * installs the Auth0 plugin, so `isAuthenticated` was always false by then and
 * the block never executed on a cold load.
 *
 * Global `beforeEach` hooks run before any route's `beforeEnter`, so priming here
 * covers every authenticated route rather than only the one feature that happened
 * to notice. It costs a silent-auth call on the first navigation of a load and
 * nothing thereafter (`ensureToken` returns immediately once the store is warm) —
 * and main.js makes that same call before mounting, so the work is moved earlier,
 * not duplicated.
 *
 * The FIRST navigation of a launch waits at most the launch budget
 * (src/utils/launch-auth-budget.js). A stalled refresh to Auth0 takes ~30 s, and
 * this wait sits inside `router.isReady()`, so an unbounded one kept the app
 * blank for that long. Priming carries on past the budget and commits its token
 * when it lands. Later navigations wait in full, as before: by then the screen
 * is up, and the store normally holds a token so they never ask.
 *
 * @param {Object} deps
 * @param {() => Promise<string|undefined>} deps.ensureToken - primes the store token; must not throw
 * @param {(path: string) => boolean} deps.isPublicPath - true for routes reachable signed-out
 * @param {(from: Object) => boolean} [deps.isInitialNavigation] - true for a launch's first navigation
 * @param {{ race: (p: Promise<unknown>) => Promise<unknown> }} [deps.launchBudget] - bounds that first wait
 * @returns {(to: Object, from: Object) => Promise<void>} a Vue Router beforeEach hook
 */
export function createTokenPrimingGuard({
  ensureToken,
  isPublicPath,
  isInitialNavigation = () => false,
  launchBudget,
}) {
  return async (to, from) => {
    // Signed-out visitors legitimately have no session. Asking Auth0 for a token
    // on the login or registration screens buys a guaranteed rejection.
    if (isPublicPath(to.path)) return;
    const priming = ensureToken();
    if (launchBudget && isInitialNavigation(from)) {
      await launchBudget.race(priming);
      return;
    }
    await priming;
  };
}
